import React, { useEffect, useMemo, useRef, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowRightFromBracket, faPlus, faSearch, faUserPlus, faVideo } from '@fortawesome/free-solid-svg-icons'
import { getAuth, onAuthStateChanged } from 'firebase/auth'
import { addDoc, collection, deleteDoc, doc, getDocs, onSnapshot, query, serverTimestamp, setDoc, where } from 'firebase/firestore'
import { getDatabase, onValue, push, ref, serverTimestamp as realtimeTimestamp, set, update } from 'firebase/database'
import { useLocation, useNavigate } from 'react-router-dom'
import swal from 'sweetalert'
import { db as firestoreDb } from '../firebase'
import sampleImage from '../Images/defprofile.png'

const DEFAULT_CHANNEL_NAMES = ['genral', 'general']
const REACTION_OPTIONS = ['👍', '❤️', '😂', '🎯', '🔥']
const DM_SIDEBAR_CACHE_KEY = 'ubunifu-instructor-workspace-dm-cache-v1'

const readDMSidebarCache = (userId) => {
  if (typeof window === 'undefined' || !userId) return null
  try {
    const rawCache = window.sessionStorage.getItem(DM_SIDEBAR_CACHE_KEY)
    if (!rawCache) return null
    const parsed = JSON.parse(rawCache)
    return parsed?.[userId] || null
  } catch (error) {
    return null
  }
}

const writeDMSidebarCache = (userId, value) => {
  if (typeof window === 'undefined' || !userId) return
  try {
    const rawCache = window.sessionStorage.getItem(DM_SIDEBAR_CACHE_KEY)
    const parsed = rawCache ? JSON.parse(rawCache) : {}
    parsed[userId] = value
    window.sessionStorage.setItem(DM_SIDEBAR_CACHE_KEY, JSON.stringify(parsed))
  } catch (error) {
    // Ignore cache failures and continue with live state.
  }
}

const getTimestamp = (value) => {
  if (!value) return 0
  if (typeof value === 'number') return value
  if (value?.toMillis) return value.toMillis()
  if (value?.seconds) return value.seconds * 1000
  return 0
}

const sanitizeMessageHtml = (input) => {
  if (!input) return ''
  const parser = new DOMParser()
  const doc = parser.parseFromString(input, 'text/html')
  const allowedTags = new Set(['b', 'strong', 'i', 'em', 'u', 's', 'code', 'pre', 'br', 'p', 'div', 'ul', 'ol', 'li', 'blockquote', 'span'])

  const walk = (node) => {
    Array.from(node.childNodes).forEach((child) => {
      if (child.nodeType === Node.ELEMENT_NODE) {
        const tag = child.tagName.toLowerCase()
        if (!allowedTags.has(tag)) {
          const fragment = doc.createDocumentFragment()
          while (child.firstChild) fragment.appendChild(child.firstChild)
          child.replaceWith(fragment)
          return
        }
        Array.from(child.attributes).forEach((attr) => child.removeAttribute(attr.name))
        walk(child)
      } else if (child.nodeType === Node.COMMENT_NODE) {
        child.remove()
      }
    })
  }

  walk(doc.body)
  return doc.body.innerHTML
}

const getPlainTextFromHtml = (input) => {
  if (!input) return ''
  const parser = new DOMParser()
  const doc = parser.parseFromString(input, 'text/html')
  return (doc.body.textContent || '').replace(/\u00a0/g, ' ')
}

const buildDirectMessageSummary = (allChats, currentUserId, getMessageTimestamp) => {
  const conversationMap = new Map()
  const seenMessageIds = new Set()

  Object.entries(allChats || {}).forEach(([chatKey, messagesObj]) => {
    const participants = chatKey.split('_')
    if (!participants.includes(currentUserId)) return
    const otherUserId = participants.find((id) => id !== currentUserId)
    if (!otherUserId) return

    const conversationKey = [currentUserId, otherUserId].sort().join('__')
    const currentSummary = conversationMap.get(otherUserId) || {
      unreadCount: 0,
      lastMessage: null
    }

    Object.entries(messagesObj || {}).forEach(([messageKey, message]) => {
      const uniqueMessageId = `${conversationKey}__${message?.messageId || messageKey}`
      if (seenMessageIds.has(uniqueMessageId)) return
      seenMessageIds.add(uniqueMessageId)

      if (message?.receiverId === currentUserId && !message?.isRead) {
        currentSummary.unreadCount += 1
      }

      if (!currentSummary.lastMessage || getMessageTimestamp(message) > getMessageTimestamp(currentSummary.lastMessage)) {
        currentSummary.lastMessage = message
      }
    })

    conversationMap.set(otherUserId, currentSummary)
  })

  const unreadMap = {}
  const lastMap = {}

  conversationMap.forEach((summary, otherUserId) => {
    unreadMap[otherUserId] = summary.unreadCount || 0
    if (summary.lastMessage) {
      lastMap[otherUserId] = {
        text: summary.lastMessage.text || '',
        timestamp: getMessageTimestamp(summary.lastMessage),
        senderId: summary.lastMessage.senderId || ''
      }
    }
  })

  return {
    userIds: Array.from(conversationMap.keys()),
    unreadMap,
    lastMap
  }
}

function WorkspaceComp() {
  const auth = getAuth()
  const realtimeDb = getDatabase()
  const navigate = useNavigate()
  const location = useLocation()
  const messagesEndRef = useRef(null)
  const editorRef = useRef(null)
  const longPressTimerRef = useRef(null)
  const pendingReactionOverridesRef = useRef({})

  const [currentUserId, setCurrentUserId] = useState('')
  const [currentUserName, setCurrentUserName] = useState('Instructor')
  const [currentUserAvatar, setCurrentUserAvatar] = useState(sampleImage)
  const [channels, setChannels] = useState([])
  const [students, setStudents] = useState([])
  const [dmUsers, setDmUsers] = useState([])
  const [selectedChat, setSelectedChat] = useState(null)
  const [messages, setMessages] = useState([])
  const [newMessage, setNewMessage] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [statusMap, setStatusMap] = useState({})
  const [channelMessagesMap, setChannelMessagesMap] = useState({})
  const [channelReadAt, setChannelReadAt] = useState({})
  const [channelReadMap, setChannelReadMap] = useState({})
  const [channelUnread, setChannelUnread] = useState({})
  const [dmUnread, setDmUnread] = useState({})
  const [dmLastMap, setDmLastMap] = useState({})
  const [instructorDirectory, setInstructorDirectory] = useState(new Map())
  const [activeReactionPickerId, setActiveReactionPickerId] = useState('')
  const [reactionDocuments, setReactionDocuments] = useState({})
  const [pendingReactionOverrides, setPendingReactionOverrides] = useState({})
  const [pendingDMTarget, setPendingDMTarget] = useState(() => location.state?.openDMUserId || '')

  const isDefaultChannel = (channel) => DEFAULT_CHANNEL_NAMES.includes((channel?.teamName || '').trim().toLowerCase())

  const filteredChannels = useMemo(() => {
    const term = searchTerm.trim().toLowerCase()
    const list = channels.filter((channel) => {
      const isOpenChannel = isDefaultChannel(channel) || !Array.isArray(channel.members) || channel.members.length === 0
      return isOpenChannel || channel.members.includes(currentUserId)
    })

    return list
      .filter((channel) => !term || (channel.teamName || '').toLowerCase().includes(term))
      .sort((a, b) => {
        const aDefault = isDefaultChannel(a)
        const bDefault = isDefaultChannel(b)
        if (aDefault === bDefault) return (a.teamName || '').localeCompare(b.teamName || '')
        return aDefault ? -1 : 1
      })
  }, [channels, currentUserId, searchTerm])

  const mergedDMs = useMemo(() => {
    const map = new Map()
    students.forEach((student) => map.set(student.userId, student))
    dmUsers.forEach((student) => map.set(student.userId, student))
    return Array.from(map.values())
  }, [students, dmUsers])

  const dmUserIds = useMemo(() => (
    new Set(dmUsers.map((user) => user.userId).filter(Boolean))
  ), [dmUsers])

  const filteredDMs = useMemo(() => {
    const term = searchTerm.trim().toLowerCase()
    return mergedDMs
      .filter((student) => !term || `${student.bio || ''} ${student.course || ''}`.toLowerCase().includes(term))
      .sort((a, b) => {
        const aInteracted = Boolean(dmLastMap[a.userId]) || dmUserIds.has(a.userId)
        const bInteracted = Boolean(dmLastMap[b.userId]) || dmUserIds.has(b.userId)
        if (aInteracted !== bInteracted) return aInteracted ? -1 : 1
        const aLast = dmLastMap[a.userId]?.timestamp || 0
        const bLast = dmLastMap[b.userId]?.timestamp || 0
        if (aLast !== bLast) return bLast - aLast
        const aUnread = dmUnread[a.userId] || 0
        const bUnread = dmUnread[b.userId] || 0
        if (aUnread !== bUnread) return bUnread - aUnread
        return (a.bio || '').localeCompare(b.bio || '')
      })
  }, [mergedDMs, dmLastMap, dmUnread, dmUserIds, searchTerm])

  const sidebarDMs = useMemo(() => {
    const map = new Map()
    mergedDMs.forEach((student) => {
      if (student?.userId) map.set(student.userId, student)
    })

    const cachedDMState = readDMSidebarCache(currentUserId)
    ;(cachedDMState?.dmUsers || []).forEach((user) => {
      if (user?.userId && !map.has(user.userId)) map.set(user.userId, user)
    })

    const activeUserIds = new Set([
      ...Object.keys(dmLastMap || {}),
      ...Object.keys(dmUnread || {})
    ].filter(Boolean))

    activeUserIds.forEach((userId) => {
      if (map.has(userId)) return
      const instructor = instructorDirectory.get(userId)
      if (instructor) {
        map.set(userId, {
          id: `employee-${userId}`,
          userId,
          bio: instructor.bio || 'Instructor',
          profilePic: instructor.profilePic || sampleImage,
          course: 'Instructor',
          email: instructor.email || ''
        })
        return
      }

      map.set(userId, {
        id: `dm-${userId}`,
        userId,
        bio: 'Conversation',
        profilePic: sampleImage,
        course: ''
      })
    })

    const term = searchTerm.trim().toLowerCase()
    return Array.from(map.values())
      .filter((student) => !term || `${student.bio || ''} ${student.course || ''}`.toLowerCase().includes(term))
      .sort((a, b) => {
        const aInteracted = Boolean(dmLastMap[a.userId]) || dmUserIds.has(a.userId)
        const bInteracted = Boolean(dmLastMap[b.userId]) || dmUserIds.has(b.userId)
        if (aInteracted !== bInteracted) return aInteracted ? -1 : 1
        const aUnread = dmUnread[a.userId] || 0
        const bUnread = dmUnread[b.userId] || 0
        if (aUnread !== bUnread) return bUnread - aUnread
        const aLast = dmLastMap[a.userId]?.timestamp || 0
        const bLast = dmLastMap[b.userId]?.timestamp || 0
        if (aLast !== bLast) return bLast - aLast
        return (a.bio || '').localeCompare(b.bio || '')
      })
  }, [currentUserId, dmLastMap, dmUnread, dmUserIds, instructorDirectory, mergedDMs, searchTerm])

  useEffect(() => {
    setPendingDMTarget(location.state?.openDMUserId || '')
  }, [location.state])

  const memberDirectory = useMemo(() => {
    const map = new Map()
    students.forEach((student) => {
      if (student.userId) map.set(student.userId, student)
    })
    if (currentUserId) {
      map.set(currentUserId, {
        userId: currentUserId,
        bio: currentUserName,
        profilePic: currentUserAvatar
      })
    }
    return map
  }, [students, currentUserId, currentUserName, currentUserAvatar])

  const getChannelMemberIds = (channel) => {
    if (!channel) return []
    if (isDefaultChannel(channel)) return Array.from(memberDirectory.keys())
    return Array.isArray(channel.members) ? channel.members.filter(Boolean) : []
  }

  const getChannelMembers = (channel) => getChannelMemberIds(channel).map((id) => {
    const member = memberDirectory.get(id)
    return {
      id,
      name: member?.bio || 'Member',
      avatar: member?.profilePic || sampleImage,
      student: member || null
    }
  })

  const getChannelOnlineCount = (channel) => {
    const ids = getChannelMemberIds(channel)
    return {
      online: ids.filter((id) => statusMap[id]?.state === 'online').length,
      total: ids.length
    }
  }

  const formatTimestamp = (value) => {
    const ts = getTimestamp(value)
    if (!ts) return ''
    return new Date(ts).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric'
    })
  }

  const getMessageTimestamp = (message) => getTimestamp(message.timestamp) || message.clientTimestamp || 0

  const getMessagePreview = (message) => {
    if (!message?.text) return ''
    return getPlainTextFromHtml(message.text).replace(/\s+/g, ' ').trim()
  }

  const getReactionConversationKey = (chat = selectedChat) => {
    if (!chat) return ''
    if (chat.type === 'dm') {
      const ids = [currentUserId, chat.userId].filter(Boolean).sort()
      return ids.length === 2 ? `dm__${ids[0]}__${ids[1]}` : ''
    }
    return chat.channelId ? `channel__${chat.channelId}` : ''
  }

  const getReactionState = (message) => (
    reactionDocuments[message?.id]?.reactionsByUser ||
    message?.reactionsByUser ||
    {}
  )

  const getReactionSummary = (message) => {
    const entries = Object.values(getReactionState(message))
      .filter((entry) => entry?.emoji)

    const grouped = entries.reduce((accumulator, entry) => {
      const current = accumulator.get(entry.emoji) || {
        emoji: entry.emoji,
        count: 0,
        reactedByMe: false
      }
      current.count += 1
      if (entry.userId === currentUserId) current.reactedByMe = true
      accumulator.set(entry.emoji, current)
      return accumulator
    }, new Map())

    return Array.from(grouped.values())
  }

  const getMessageViewCount = (message) => {
    if (!message || message.senderId !== currentUserId) return 0

    if (selectedChat?.type === 'dm') {
      return message.isRead ? 1 : 0
    }

    if (selectedChat?.type !== 'channel') return 0

    const reads = channelReadMap[selectedChat.channelId] || {}
    const messageTimestamp = getMessageTimestamp(message)
    return Object.entries(reads).filter(([userId, readAt]) => {
      if (!userId || userId === currentUserId || userId === message.senderId) return false
      return getTimestamp(readAt) >= messageTimestamp
    }).length
  }

  const getMessageViewLabel = (message) => {
    const count = getMessageViewCount(message)
    if (count === 0) return selectedChat?.type === 'dm' ? 'Sent' : 'Not viewed yet'
    return `Seen by ${count}`
  }

  const patchLocalMessageReaction = (messageId, userId, nextValue) => {
    if (!messageId || !userId) return
    setReactionDocuments((previous) => {
      const currentDoc = previous[messageId] || {}
      const nextReactions = { ...(currentDoc.reactionsByUser || {}) }
      if (nextValue) {
        nextReactions[userId] = nextValue
      } else {
        delete nextReactions[userId]
      }
      return {
        ...previous,
        [messageId]: {
          ...currentDoc,
          reactionsByUser: nextReactions
        }
      }
    })
  }

  const applyPendingReactionOverrides = (items) => {
    const nextDocs = { ...items }
    Object.entries(pendingReactionOverridesRef.current).forEach(([messageId, override]) => {
      if (!override?.userId) return
      const currentDoc = nextDocs[messageId] || {}
      const nextReactions = { ...(currentDoc.reactionsByUser || {}) }
      if (override.nextValue) {
        nextReactions[override.userId] = override.nextValue
      } else {
        delete nextReactions[override.userId]
      }
      nextDocs[messageId] = {
        ...currentDoc,
        reactionsByUser: nextReactions
      }
    })
    return nextDocs
  }

  const reconcilePendingReactionOverrides = (items) => {
    setPendingReactionOverrides((previous) => {
      const next = { ...previous }
      Object.entries(items || {}).forEach(([messageId, reactionDoc]) => {
        const override = previous[messageId]
        if (!override?.userId) return
        const liveEmoji = reactionDoc?.reactionsByUser?.[override.userId]?.emoji || ''
        const pendingEmoji = override?.nextValue?.emoji || ''
        if (liveEmoji === pendingEmoji) {
          delete next[messageId]
        }
      })
      return next
    })
  }

  const clearLongPressTimer = () => {
    if (!longPressTimerRef.current) return
    window.clearTimeout(longPressTimerRef.current)
    longPressTimerRef.current = null
  }

  const openReactionPicker = (messageId) => {
    if (!messageId) return
    setActiveReactionPickerId(messageId)
  }

  const closeReactionPicker = (messageId) => {
    setActiveReactionPickerId((previous) => (previous === messageId ? '' : previous))
  }

  const scheduleReactionPicker = (messageId) => {
    clearLongPressTimer()
    longPressTimerRef.current = window.setTimeout(() => {
      setActiveReactionPickerId(messageId)
      longPressTimerRef.current = null
    }, 420)
  }

  const isInstructorMessage = (message) => {
    if (!message) return false
    if (String(message.senderRole || '').toLowerCase() === 'instructor') return true
    if (message.senderId === currentUserId) return true
    return instructorDirectory.has(message.senderId)
  }

  const updateEditorState = () => {
    if (!editorRef.current) return
    setNewMessage(editorRef.current.innerHTML)
  }

  const applyInlineStyle = (command) => {
    if (!editorRef.current) return
    editorRef.current.focus()
    document.execCommand(command, false, null)
    updateEditorState()
  }

  const insertCodeBlock = () => {
    if (!editorRef.current) return
    editorRef.current.focus()
    document.execCommand('insertHTML', false, '<pre><code>\n</code></pre>')
    updateEditorState()
  }

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      const uid = user?.uid || ''
      setCurrentUserId(uid)

      if (!uid) {
        setDmUsers([])
        setDmLastMap({})
        return
      }

      const cachedDMState = readDMSidebarCache(uid)
      if (cachedDMState?.dmLastMap) {
        setDmLastMap(cachedDMState.dmLastMap)
      }
      if (Array.isArray(cachedDMState?.dmUsers)) {
        setDmUsers(cachedDMState.dmUsers)
      }

      const employeeSnapshot = await getDocs(query(collection(firestoreDb, 'Employees'), where('docId', '==', uid)))
      const employeeData = employeeSnapshot.docs[0]?.data()
      setCurrentUserName(employeeData?.employeeName || user.displayName || user.email || 'Instructor')
      setCurrentUserAvatar(employeeData?.profilePic || user.photoURL || sampleImage)
    })

    return () => unsubscribe()
  }, [auth])

  useEffect(() => {
    const fetchChannels = async () => {
      const snapshot = await getDocs(query(collection(firestoreDb, 'Teams')))
      const teamItems = snapshot.docs.map((teamDoc) => ({ id: teamDoc.id, ...teamDoc.data() }))
      const hasDefault = teamItems.some((team) => DEFAULT_CHANNEL_NAMES.includes((team.teamName || '').trim().toLowerCase()))

      if (!hasDefault) {
        try {
          const defaultRef = await addDoc(collection(firestoreDb, 'Teams'), {
            teamName: 'genral',
            teamDesc: 'Default channel for everyone',
            members: [],
            createdAt: serverTimestamp()
          })
          teamItems.unshift({ id: defaultRef.id, teamName: 'genral', teamDesc: 'Default channel for everyone', members: [] })
        } catch (error) {
          // Continue with existing channels if permissions prevent creating the default one.
        }
      }

      setChannels(teamItems)
    }

    const fetchStudents = async () => {
      const snapshot = await getDocs(collection(firestoreDb, 'users'))
      const items = snapshot.docs
        .map((studentDoc) => ({ id: studentDoc.id, ...studentDoc.data() }))
        .filter((student) => {
          const status = String(student.userStatus || student.Status || '').toLowerCase()
          return student.userId && student.userId !== auth.currentUser?.uid && (status === 'student' || status === 'active')
        })
      setStudents(items)
    }

    const fetchInstructors = async () => {
      const snapshot = await getDocs(collection(firestoreDb, 'Employees'))
      const directory = new Map()
      snapshot.docs.forEach((employeeDoc) => {
        const employee = employeeDoc.data()
        const employeeId = employee?.docId
        if (!employeeId) return
        directory.set(employeeId, {
          userId: employeeId,
          bio: employee?.employeeName || 'Instructor',
          profilePic: employee?.profilePic || sampleImage,
          email: employee?.email || ''
        })
      })
      setInstructorDirectory(directory)
    }

    fetchChannels()
    fetchStudents()
    fetchInstructors()
  }, [auth.currentUser?.uid])

  useEffect(() => {
    const statusRef = ref(realtimeDb, 'status')
    const unsubscribe = onValue(statusRef, (snapshot) => {
      setStatusMap(snapshot.exists() ? snapshot.val() : {})
    })
    return () => unsubscribe()
  }, [realtimeDb])

  useEffect(() => {
    if (!currentUserId) return
    const chatsRef = ref(realtimeDb, 'chats')
    const unsubscribe = onValue(chatsRef, async (snapshot) => {
      if (!snapshot.exists()) {
        setDmUsers([])
        setDmUnread({})
        setDmLastMap({})
        return
      }

      const allChats = snapshot.val()
      const { userIds, unreadMap, lastMap } = buildDirectMessageSummary(allChats, currentUserId, getMessageTimestamp)
      const studentMap = new Map(students.map((student) => [student.userId, student]))
      const cachedDmUsers = readDMSidebarCache(currentUserId)?.dmUsers || []
      const existingDmMap = new Map(cachedDmUsers.map((user) => [user.userId, user]))

      const userSnapshots = await Promise.all(
        userIds.map((id) => getDocs(query(collection(firestoreDb, 'users'), where('userId', '==', id))))
      )

      const resolvedUsers = userIds.map((id, index) => {
        if (studentMap.has(id)) return studentMap.get(id)
        if (existingDmMap.has(id)) return existingDmMap.get(id)

        const userSnapshot = userSnapshots[index]
        if (userSnapshot?.docs?.length > 0) {
          const userDoc = userSnapshot.docs[0]
          const data = userDoc.data()
          return {
            id: userDoc.id,
            ...data
          }
        }

        const instructor = instructorDirectory.get(id)
        if (instructor) {
          return {
            id: `employee-${id}`,
            userId: id,
            bio: instructor.bio || 'Instructor',
            profilePic: instructor.profilePic || sampleImage,
            course: 'Instructor',
            email: instructor.email || ''
          }
        }

        return {
          id: `dm-${id}`,
          userId: id,
          bio: 'Conversation',
          profilePic: sampleImage,
          course: ''
        }
      }).filter(Boolean)

      setDmUnread(unreadMap)
      setDmLastMap(lastMap)
      setDmUsers(resolvedUsers)
      writeDMSidebarCache(currentUserId, {
        dmUsers: resolvedUsers.map((user) => ({
          id: user.id,
          userId: user.userId,
          bio: user.bio,
          profilePic: user.profilePic || sampleImage,
          course: user.course || '',
          email: user.email || ''
        })),
        dmLastMap: lastMap,
        updatedAt: Date.now()
      })
    })

    return () => unsubscribe()
  }, [currentUserId, realtimeDb, students, instructorDirectory])

  useEffect(() => {
    if (!currentUserId || channels.length === 0) return

    const unsubscribers = channels.flatMap((channel) => {
      const readRef = ref(realtimeDb, `channelReads/${channel.id}`)
      const readUnsubscribe = onValue(readRef, (snapshot) => {
        const nextValue = snapshot.exists() ? snapshot.val() : {}
        setChannelReadMap((previous) => ({ ...previous, [channel.id]: nextValue }))
        setChannelReadAt((previous) => ({ ...previous, [channel.id]: nextValue?.[currentUserId] || 0 }))
      })

      const channelRef = ref(realtimeDb, `channelChats/${channel.id}`)
      const channelUnsubscribe = onValue(channelRef, (snapshot) => {
        const items = snapshot.exists() ? Object.values(snapshot.val()) : []
        setChannelMessagesMap((previous) => ({ ...previous, [channel.id]: items }))
      })

      return [readUnsubscribe, channelUnsubscribe]
    })

    return () => unsubscribers.forEach((unsubscribe) => unsubscribe())
  }, [channels, currentUserId, realtimeDb])

  useEffect(() => {
    const unreadMap = {}
    channels.forEach((channel) => {
      const channelMessages = channelMessagesMap[channel.id] || []
      const lastRead = channelReadAt[channel.id] || 0
      unreadMap[channel.id] = channelMessages.filter((message) => (
        getMessageTimestamp(message) > lastRead && message.senderId !== currentUserId
      )).length
    })
    setChannelUnread(unreadMap)
  }, [channels, channelMessagesMap, channelReadAt, currentUserId])

  useEffect(() => {
    if (!selectedChat || !currentUserId) return

    if (selectedChat.type === 'dm') {
      const chatRef = ref(realtimeDb, `chats/${currentUserId}_${selectedChat.userId}`)
      const unsubscribe = onValue(chatRef, (snapshot) => {
        const formatted = snapshot.exists()
          ? Object.entries(snapshot.val()).map(([id, value]) => ({ id, ...value }))
          : []
        formatted.sort((a, b) => getMessageTimestamp(a) - getMessageTimestamp(b))
        setMessages(formatted)

        formatted.forEach((message) => {
          if (message.receiverId === currentUserId && !message.isRead) {
            update(ref(realtimeDb, `chats/${currentUserId}_${selectedChat.userId}/${message.id}`), {
              isRead: true,
              readAt: realtimeTimestamp()
            })
            update(ref(realtimeDb, `chats/${selectedChat.userId}_${currentUserId}/${message.messageId || message.id}`), {
              isRead: true,
              readAt: realtimeTimestamp()
            })
          }
        })
      })
      return () => unsubscribe()
    }

    const channelRef = ref(realtimeDb, `channelChats/${selectedChat.channelId}`)
    const unsubscribe = onValue(channelRef, (snapshot) => {
      const formatted = snapshot.exists()
        ? Object.entries(snapshot.val()).map(([id, value]) => ({ id, ...value }))
        : []
      formatted.sort((a, b) => getMessageTimestamp(a) - getMessageTimestamp(b))
      setMessages(formatted)
    })
    set(ref(realtimeDb, `channelReads/${selectedChat.channelId}/${currentUserId}`), realtimeTimestamp())
    return () => unsubscribe()
  }, [selectedChat, currentUserId, realtimeDb])

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  useEffect(() => () => clearLongPressTimer(), [])

  useEffect(() => {
    pendingReactionOverridesRef.current = pendingReactionOverrides
  }, [pendingReactionOverrides])

  useEffect(() => {
    const conversationKey = getReactionConversationKey()
    if (!conversationKey) {
      setReactionDocuments({})
      return undefined
    }

    const reactionsRef = collection(firestoreDb, 'workspaceMessageReactions', conversationKey, 'entries')
    const unsubscribe = onSnapshot(reactionsRef, (snapshot) => {
      const nextDocs = {}
      snapshot.forEach((reactionDoc) => {
        const reaction = reactionDoc.data()
        if (!reaction?.messageId || !reaction?.userId) return
        if (!nextDocs[reaction.messageId]) {
          nextDocs[reaction.messageId] = { reactionsByUser: {} }
        }
        nextDocs[reaction.messageId].reactionsByUser[reaction.userId] = reaction
      })
      reconcilePendingReactionOverrides(nextDocs)
      setReactionDocuments(applyPendingReactionOverrides(nextDocs))
    })

    return () => unsubscribe()
  }, [selectedChat, currentUserId])

  const selectChannel = (channel) => {
    const isOpenChannel = isDefaultChannel(channel) || !Array.isArray(channel.members) || channel.members.length === 0
    if (!isOpenChannel && !channel.members.includes(currentUserId)) {
      swal('Access denied', 'You are not a member of this channel yet.', 'warning')
      return
    }

    setSelectedChat({
      type: 'channel',
      channelId: channel.id,
      name: channel.teamName,
      description: channel.teamDesc
    })
  }

  const selectDM = (student) => {
    setDmUsers((previous) => {
      if (!student?.userId || previous.some((item) => item.userId === student.userId)) return previous
      const nextUsers = [
        ...previous,
        {
          id: student.id || `dm-${student.userId}`,
          userId: student.userId,
          bio: student.bio || 'Student',
          profilePic: student.profilePic || sampleImage,
          course: student.course || '',
          email: student.email || ''
        }
      ]
      writeDMSidebarCache(currentUserId, {
        dmUsers: nextUsers.map((user) => ({
          id: user.id,
          userId: user.userId,
          bio: user.bio,
          profilePic: user.profilePic || sampleImage,
          course: user.course || '',
          email: user.email || ''
        })),
        dmLastMap,
        updatedAt: Date.now()
      })
      return nextUsers
    })

    setSelectedChat({
      type: 'dm',
      userId: student.userId,
      name: student.bio || 'Student',
      avatar: student.profilePic || sampleImage,
      course: student.course || ''
    })
  }

  useEffect(() => {
    if (!pendingDMTarget) return

    const targetStudent = mergedDMs.find((student) => student.userId === pendingDMTarget)
    if (!targetStudent) {
      if (location.state?.openDMUserId && location.state?.openDMName) {
        selectDM({
          userId: location.state.openDMUserId,
          bio: location.state.openDMName,
          profilePic: location.state.openDMAvatar || sampleImage,
          course: location.state.openDMCourse || ''
        })
        setPendingDMTarget('')
      }
      return
    }

    selectDM(targetStudent)
    setPendingDMTarget('')
  }, [pendingDMTarget, mergedDMs, location.state])

  const openStudentProfile = (student, event) => {
    event?.stopPropagation?.()
    event?.preventDefault?.()

    const studentId = student?.userId || student?.id
    if (!studentId || studentId === currentUserId) return

    navigate(`/student-profile/${studentId}`, {
      state: {
        searchStudentPreview: {
          studentId,
          name: student?.bio || student?.name || 'Student',
          email: student?.userEmail || student?.email || '',
          avatar: student?.profilePic || student?.avatar || sampleImage,
          course: student?.course || ''
        }
      }
    })
  }

  const handleItemKeySelect = (event, callback) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      callback()
    }
  }

  const sendMessage = () => {
    const cleanHtml = sanitizeMessageHtml(newMessage)
    const plainText = getPlainTextFromHtml(cleanHtml).trim()
    if (!plainText || !selectedChat) {
      swal('Error', 'Message cannot be empty!', 'error')
      return
    }

    if (selectedChat.type === 'dm') {
      const localTimestamp = Date.now()
      const messageData = {
        text: cleanHtml,
        senderId: currentUserId,
        receiverId: selectedChat.userId,
        timestamp: realtimeTimestamp(),
        clientTimestamp: localTimestamp,
        isRead: false,
        senderRole: 'instructor',
        reactionsByUser: {}
      }
      const nextLastMap = {
        ...dmLastMap,
        [selectedChat.userId]: { text: cleanHtml, timestamp: localTimestamp, senderId: currentUserId }
      }
      const nextDmUsers = dmUsers.some((user) => user.userId === selectedChat.userId)
        ? dmUsers
        : [
            ...dmUsers,
            {
              id: `dm-${selectedChat.userId}`,
              userId: selectedChat.userId,
              bio: selectedChat.name || 'Student',
              profilePic: selectedChat.avatar || sampleImage,
              course: selectedChat.course || '',
              email: ''
            }
          ]
      const chatRef = ref(realtimeDb, `chats/${currentUserId}_${selectedChat.userId}`)
      const newMsgRef = push(chatRef)
      const fullMessage = { ...messageData, messageId: newMsgRef.key }
      set(newMsgRef, fullMessage)
      set(ref(realtimeDb, `chats/${selectedChat.userId}_${currentUserId}/${newMsgRef.key}`), fullMessage)
      setDmLastMap(nextLastMap)
      setDmUsers(nextDmUsers)
      writeDMSidebarCache(currentUserId, {
        dmUsers: nextDmUsers.map((user) => ({
          id: user.id,
          userId: user.userId,
          bio: user.bio,
          profilePic: user.profilePic || sampleImage,
          course: user.course || '',
          email: user.email || ''
        })),
        dmLastMap: nextLastMap,
        updatedAt: Date.now()
      })
    } else {
      push(ref(realtimeDb, `channelChats/${selectedChat.channelId}`), {
        text: cleanHtml,
        senderId: currentUserId,
        senderName: currentUserName || 'Instructor',
        senderRole: 'instructor',
        timestamp: realtimeTimestamp(),
        clientTimestamp: Date.now(),
        reactionsByUser: {}
      })
    }

    setNewMessage('')
    if (editorRef.current) editorRef.current.innerHTML = ''
  }

  const openCreateChannel = async () => {
    const name = await swal({
      text: 'Channel name',
      content: 'input',
      button: { text: 'Next', closeModal: true }
    })
    if (!name) return

    const desc = await swal({
      text: 'Channel description',
      content: 'input',
      button: { text: 'Create channel', closeModal: true }
    })

    try {
      const newChannelRef = await addDoc(collection(firestoreDb, 'Teams'), {
        teamName: name,
        teamDesc: desc || 'Channel discussion',
        createdAt: serverTimestamp(),
        createdBy: currentUserId,
        members: [currentUserId].filter(Boolean)
      })
      setChannels((previous) => [...previous, {
        id: newChannelRef.id,
        teamName: name,
        teamDesc: desc || 'Channel discussion',
        members: [currentUserId].filter(Boolean)
      }])
      swal('Success', 'Channel created', 'success')
    } catch (error) {
      swal('Error', 'Failed to create channel', 'error')
    }
  }

  const openStudentPicker = async () => {
    const availableStudents = students.filter((student) => student.userId)
    if (availableStudents.length === 0) {
      swal('No students', 'No active students found.', 'info')
      return
    }

    const container = document.createElement('div')
    container.className = 'workspaceStudentModal'
    const searchInput = document.createElement('input')
    searchInput.className = 'workspaceStudentSearch'
    searchInput.placeholder = 'Search by student name or course'
    const list = document.createElement('div')
    list.className = 'workspaceStudentList'
    container.appendChild(searchInput)
    container.appendChild(list)

    const renderList = (term = '') => {
      const normalizedTerm = term.trim().toLowerCase()
      list.innerHTML = ''
      availableStudents
        .filter((student) => !normalizedTerm || `${student.bio || ''} ${student.course || ''}`.toLowerCase().includes(normalizedTerm))
        .forEach((student) => {
          const row = document.createElement('div')
          row.className = 'workspaceStudentRow'
          const image = document.createElement('img')
          image.src = student.profilePic || sampleImage
          image.alt = student.bio || 'Student'
          const info = document.createElement('div')
          info.className = 'workspaceStudentInfo'
          const name = document.createElement('div')
          name.className = 'workspaceStudentName'
          name.textContent = student.bio || 'Student'
          name.tabIndex = 0
          name.setAttribute('role', 'button')
          name.addEventListener('click', (event) => openStudentProfile(student, event))
          name.addEventListener('keydown', (event) => handleItemKeySelect(event, () => openStudentProfile(student, event)))
          const course = document.createElement('div')
          course.className = 'workspaceStudentMeta'
          course.textContent = student.course || 'Course not set'
          const action = document.createElement('button')
          action.className = 'workspaceStudentAdd'
          action.textContent = 'Open chat'
          action.addEventListener('click', () => {
            selectDM(student)
            swal.close()
          })
          info.appendChild(name)
          info.appendChild(course)
          row.appendChild(image)
          row.appendChild(info)
          row.appendChild(action)
          list.appendChild(row)
        })
      if (!list.children.length) {
        const empty = document.createElement('div')
        empty.className = 'workspaceStudentEmpty'
        empty.textContent = 'No students match that search.'
        list.appendChild(empty)
      }
    }

    searchInput.addEventListener('input', (event) => renderList(event.target.value))
    renderList('')

    swal({
      title: 'Start a conversation',
      content: container,
      buttons: { cancel: { text: 'Exit', visible: true } }
    })
    window.setTimeout(() => searchInput.focus(), 0)
  }

  const startVideoCall = () => {
    if (!selectedChat) return
    if (selectedChat.type === 'dm') {
      const ids = [currentUserId, selectedChat.userId].filter(Boolean).sort()
      const roomId = `dm-${ids[0]}-${ids[1]}`
      navigate('/class-session', {
        state: {
          myData: roomId,
          startedBy: currentUserId,
          startedByName: currentUserName,
          channelName: selectedChat.name || 'Direct message'
        }
      })
      return
    }

    navigate('/class-session', {
      state: {
        myData: `channel-${selectedChat.channelId}`,
        startedBy: currentUserId,
        startedByName: currentUserName,
        channelName: selectedChat.name || 'Channel'
      }
    })
  }

  const toggleReaction = (message, emoji) => {
    if (!selectedChat || !currentUserId || !message?.id) return
    const currentReaction = getReactionState(message)?.[currentUserId]?.emoji || ''
    const nextValue = currentReaction === emoji
      ? null
      : {
          emoji,
          userId: currentUserId,
          name: currentUserName || 'Instructor',
          reactedAt: Date.now()
        }

    setPendingReactionOverrides((previous) => ({
      ...previous,
      [message.id]: {
        userId: currentUserId,
        nextValue
      }
    }))
    patchLocalMessageReaction(message.id, currentUserId, nextValue)
    const conversationKey = getReactionConversationKey()
    if (!conversationKey) return
    const reactionDocRef = doc(firestoreDb, 'workspaceMessageReactions', conversationKey, 'entries', `${message.id}__${currentUserId}`)
    if (!nextValue) {
      deleteDoc(reactionDocRef)
      return
    }
    setDoc(reactionDocRef, {
      messageId: message.id,
      userId: currentUserId,
      ...nextValue,
      updatedAt: serverTimestamp()
    }, { merge: true })
  }

  const handleReactionPointerDown = (event, message, emoji) => {
    event.preventDefault()
    event.stopPropagation()
    toggleReaction(message, emoji)
  }

  const selectedChannelData = selectedChat?.type === 'channel'
    ? channels.find((channel) => channel.id === selectedChat.channelId)
    : null
  const selectedChannelMembers = selectedChannelData ? getChannelMembers(selectedChannelData) : []

  return (
    <div className='workspaceShell'>
      <aside className='workspaceSidebar'>
        <div className='workspaceBrand'>
          <div>
            <h5>Workspace</h5>
            <p>Students & Channels</p>
          </div>
          <div className='workspaceBrandActions'>
            <button className='workspaceAddBtn' onClick={openCreateChannel}>
              <FontAwesomeIcon icon={faPlus} />
            </button>
            <button className='workspaceExitBtn' onClick={() => navigate('/dashboard')}>
              <FontAwesomeIcon icon={faArrowRightFromBracket} />
              Exit
            </button>
          </div>
        </div>

        <div className='workspaceSearch'>
          <FontAwesomeIcon icon={faSearch} />
          <input
            placeholder='Search students or channels'
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
          />
        </div>

        <div className='workspaceSection'>
          <div className='workspaceSectionHeader'>
            <h6>Channels</h6>
            <button className='workspaceIconBtn' onClick={openCreateChannel}>
              <FontAwesomeIcon icon={faPlus} />
            </button>
          </div>
          <div className='workspaceList'>
            {filteredChannels.map((channel) => {
              const presence = getChannelOnlineCount(channel)
              const members = getChannelMembers(channel).slice(0, 3)
              return (
                <button
                  className={selectedChat?.channelId === channel.id ? 'workspaceItem active' : 'workspaceItem'}
                  key={channel.id}
                  onClick={() => selectChannel(channel)}
                >
                  <span className='workspaceHash'>#</span>
                  <div className='workspaceChannelLabel'>
                    <div className='workspaceChannelText'>
                      <span className='workspaceChannelName'>{channel.teamName}</span>
                      <span className='workspaceChannelMeta'>{presence.online} online / {presence.total} members</span>
                    </div>
                    {channelUnread[channel.id] > 0 && <span className='workspaceUnread'>{channelUnread[channel.id]}</span>}
                  </div>
                  <div className='workspaceChannelAvatars'>
                    {members.map((member) => (
                      <img className='workspaceChannelAvatar' src={member.avatar} alt={member.name} key={`${channel.id}-${member.id}`} />
                    ))}
                  </div>
                </button>
              )
            })}
            {filteredChannels.length === 0 && <p className='workspaceEmpty'>No channels found.</p>}
          </div>
        </div>

        <div className='workspaceSection'>
          <div className='workspaceSectionHeader'>
            <h6>Direct Messages</h6>
            <button className='workspaceIconBtn' onClick={openStudentPicker}>
              <FontAwesomeIcon icon={faUserPlus} />
            </button>
          </div>
          <div className='workspaceList'>
            {sidebarDMs.map((student) => (
              <div
                className={selectedChat?.userId === student.userId ? 'workspaceItem workspaceSelectableItem active' : 'workspaceItem workspaceSelectableItem'}
                key={student.userId}
                onClick={() => selectDM(student)}
                onKeyDown={(event) => handleItemKeySelect(event, () => selectDM(student))}
                role='button'
                tabIndex={0}
              >
                <button
                  type='button'
                  className='workspaceAvatarBtn'
                  onClick={(event) => openStudentProfile(student, event)}
                  aria-label={`View ${student.bio || 'student'} profile`}
                >
                  <img src={student.profilePic || sampleImage} alt='' />
                </button>
                <div>
                  <button
                    type='button'
                    className='workspaceIdentityBtn'
                    onClick={(event) => openStudentProfile(student, event)}
                  >
                    {student.bio || 'Student'}
                  </button>
                  {dmLastMap[student.userId] ? (
                    <span className='workspaceLastMessage'>
                      {dmLastMap[student.userId].senderId === currentUserId ? 'You: ' : ''}
                      {getMessagePreview(dmLastMap[student.userId]) || student.course}
                    </span>
                  ) : (
                    <span>{student.course || 'Course not set'}</span>
                  )}
                </div>
                {dmUnread[student.userId] > 0 && (
                  <span
                    className='workspaceUnread'
                    title={`${dmUnread[student.userId]} unread message${dmUnread[student.userId] > 1 ? 's' : ''}`}
                    aria-label={`${dmUnread[student.userId]} unread message${dmUnread[student.userId] > 1 ? 's' : ''}`}
                  >
                    {dmUnread[student.userId]}
                  </span>
                )}
              </div>
            ))}
            {sidebarDMs.length === 0 && <p className='workspaceEmpty'>No direct messages yet.</p>}
          </div>
        </div>
      </aside>

      <section className='workspaceMain'>
        {!selectedChat && (
          <div className='workspaceEmptyState'>
            <h3>Select a conversation</h3>
            <p>Choose a channel or student to start chatting.</p>
            <div className='workspaceEmptyActions'>
              <button onClick={openCreateChannel}>Create channel</button>
              <button className='secondary' onClick={openStudentPicker}>Add student</button>
            </div>
          </div>
        )}

        {selectedChat && (
          <>
            <div className='workspaceTopBar'>
              <div>
                {selectedChat.type === 'channel' ? (
                  <h5># {selectedChat.name}</h5>
                ) : (
                  <button
                    type='button'
                    className='workspaceProfileHeadingBtn'
                    onClick={(event) => openStudentProfile(selectedChat, event)}
                  >
                    {selectedChat.name}
                  </button>
                )}
                {selectedChat.type === 'channel' ? (
                  <p>{selectedChat.description}</p>
                ) : (
                  <p>{selectedChat.course}</p>
                )}
              </div>
              <div className='workspaceTopActions'>
                {selectedChat.type === 'channel' && (
                  <div className='workspaceMemberGroup'>
                    {selectedChannelMembers.slice(0, 5).map((member) => (
                      <button
                        type='button'
                        className='workspaceMemberAvatarBtn'
                        key={member.id}
                        onClick={(event) => openStudentProfile(member.student || { userId: member.id, bio: member.name, profilePic: member.avatar }, event)}
                        title={`View ${member.name} profile`}
                      >
                        <img className='workspaceMemberAvatar' src={member.avatar} alt={member.name} />
                      </button>
                    ))}
                  </div>
                )}
                <button className='workspaceIconBtn' onClick={startVideoCall}>
                  <FontAwesomeIcon icon={faVideo} />
                </button>
              </div>
            </div>

            <div className='workspaceMessages'>
              {messages.length === 0 && (
                <div className='workspaceEmptyMessages'>
                  <p>Start the conversation.</p>
                </div>
              )}
              {messages.map((message) => {
                const reactionSummary = getReactionSummary(message)
                const showReactionTray = activeReactionPickerId === message.id
                return (
                <div
                  key={message.id}
                  className={message.senderId === currentUserId
                    ? `workspaceMessage mine${showReactionTray ? ' workspaceMessageReactionActive' : ''}`
                    : `workspaceMessage${showReactionTray ? ' workspaceMessageReactionActive' : ''}`
                  }
                  onMouseEnter={() => openReactionPicker(message.id)}
                  onMouseLeave={() => closeReactionPicker(message.id)}
                  onTouchStart={() => scheduleReactionPicker(message.id)}
                  onTouchEnd={clearLongPressTimer}
                  onTouchMove={clearLongPressTimer}
                  onTouchCancel={clearLongPressTimer}
                >
                  {selectedChat.type === 'channel' && (
                    <div className='workspaceMessageAuthorRow'>
                      {isInstructorMessage(message) ? (
                        <div className='workspaceMessageAuthor'>
                          {message.senderName || instructorDirectory.get(message.senderId)?.bio || 'Instructor'}
                        </div>
                      ) : (
                        <button
                          type='button'
                          className='workspaceMessageAuthor workspaceMessageAuthorBtn'
                          onClick={(event) => openStudentProfile(memberDirectory.get(message.senderId) || {
                            userId: message.senderId,
                            bio: message.senderName || 'Member'
                          }, event)}
                        >
                          {message.senderName || 'Member'}
                        </button>
                      )}
                      {isInstructorMessage(message) && (
                        <span className='workspaceRolePill'>Instructor</span>
                      )}
                    </div>
                  )}
                  <div className='workspaceMessageText' dangerouslySetInnerHTML={{ __html: sanitizeMessageHtml(message.text) }} />
                  {showReactionTray && (
                    <div className='workspaceMessageActions'>
                      <div className='workspaceReactionTray'>
                        {REACTION_OPTIONS.map((emoji) => {
                          const active = getReactionState(message)?.[currentUserId]?.emoji === emoji
                          return (
                          <button
                              type='button'
                              key={`${message.id}-${emoji}`}
                              className={active ? 'workspaceReactionBtn active' : 'workspaceReactionBtn'}
                              onPointerDown={(event) => handleReactionPointerDown(event, message, emoji)}
                          >
                            {emoji}
                          </button>
                          )
                        })}
                      </div>
                    </div>
                  )}
                  {reactionSummary.length > 0 && (
                    <div className='workspaceReactionSummary workspaceReactionSummaryPinned'>
                      {reactionSummary.map((entry) => (
                        <button
                          type='button'
                          key={`${message.id}-summary-${entry.emoji}`}
                          className={entry.reactedByMe ? 'workspaceReactionBubble active' : 'workspaceReactionBubble'}
                          onPointerDown={(event) => handleReactionPointerDown(event, message, entry.emoji)}
                        >
                          <span>{entry.emoji}</span>
                          <strong>{entry.count}</strong>
                        </button>
                      ))}
                    </div>
                  )}
                  {(message.timestamp || message.clientTimestamp) && (
                    <div className='workspaceMessageTime'>
                      {formatTimestamp(message.timestamp || message.clientTimestamp)}
                      {message.senderId === currentUserId && (
                        <span className='workspaceReadStatus'>{getMessageViewLabel(message)}</span>
                      )}
                    </div>
                  )}
                </div>
                )
              })}
              <div ref={messagesEndRef} />
            </div>

            <div className='workspaceComposer'>
              <div className='workspaceComposerTools'>
                <button type='button' onClick={() => applyInlineStyle('bold')}>B</button>
                <button type='button' onClick={() => applyInlineStyle('italic')}>I</button>
                <button type='button' onClick={() => applyInlineStyle('underline')}>U</button>
                <button type='button' onClick={insertCodeBlock}>Code</button>
              </div>
              <div
                className='workspaceEditor'
                ref={editorRef}
                contentEditable
                role='textbox'
                aria-multiline='true'
                data-placeholder='Write a message...'
                onInput={updateEditorState}
              />
              <button onClick={sendMessage}>Send</button>
            </div>
          </>
        )}
      </section>
    </div>
  )
}

export default WorkspaceComp
